import { TbCoins } from 'react-icons/tb'

import { TopMenu } from '../Components/TopMenu'
import { serviceFields } from '../Contracts/DictionaryFields'
import { CheckDictionaryPageAccessTask } from '../Tasks/CheckDictionaryPageAccessTask'

import type { IResourceConfig } from '@/Core/Contracts/UI'
import type { IFieldConfig } from '@/Core/Contracts/UI/Form'

/** Мінімум поля й форми, з яким працює `depFn` ядра. */
interface CodeDepFnArgs {
    field: {
        mergeInputProps?: (props: Record<string, unknown>) => void
    }
    formContext: {
        contextKey?: string
        values?: { uuid?: unknown }
    }
}

/**
 * «Код» задається лише при створенні (R-22): у ядрі код валюти — ідентифікатор
 * в адресі (`/services/currencies/{code}`). У PD поле було вимкнене навіть у
 * формі створення, і валюту з інтерфейсу створити було неможливо.
 *
 * Повертає `undefined`, а не `false`: `false` з `depFn` ядро читає як
 * «сховати поле».
 */
const lockCodeOfSavedCurrency = ({ field, formContext }: CodeDepFnArgs): void => {
    if (formContext.contextKey === 'filterFormContext') return

    const uuid = formContext.values?.uuid

    field.mergeInputProps?.({ disabled: typeof uuid === 'string' && uuid !== '' })
}

/** Службова інформація валюти ядра: аудиту «ким» у ній немає (`SPEC-007` §5.5). */
const { id, createdAt, updatedAt } = serviceFields() as Record<string, IFieldConfig>

/**
 * Довідник «Валюти» — Ship-сутність ядра, спільна для всіх тенантів
 * (`SPEC-007` §5.5, RK-01). Поля й підписи — легасі (`ListCurrencyResource.js`),
 * а не PD («Базова валюта» → «Базова»).
 *
 * - «Базова» лише для читання: базова — завжди гривня, бек відхиляє зміну
 *   (R-24) — на гривню спирається імпорт НБУ.
 * - UAH, EUR, USD бек не дає видалити чи змінити їм код (R-23).
 * - Сторінку бачить лише адміністратор (`pageConfig.accessFn`, `SPEC-007`
 *   §7.1); читати валюти для селектів може кожен. У легасі сторінка валют
 *   відкривалася будь-кому (D-32).
 */
export const ListCurrencyResource: IResourceConfig = {
    title: 'Валюти',
    fields: {
        title: {
            label: 'Назва',
            type: 'string',
            isRequired: true,
        },
        isBasic: {
            label: 'Базова',
            type: 'switch',
            isEditable: false,
            inputProps: { disabled: true },
        },
        formatting: {
            label: 'Рядок формату для виведення валюти',
            type: 'string',
            isRequired: true,
        },
        code: {
            label: 'Код',
            type: 'string',
            isRequired: true,
            depFn: lockCodeOfSavedCurrency,
        },
        sorting: {
            label: 'Сортування',
            type: 'number',
            defaultValue: 500,
        },
        ...(id ? { id } : {}),
        ...(createdAt ? { createdAt } : {}),
        ...(updatedAt ? { updatedAt } : {}),
    },
    // Колонки легасі (`SPEC-007` §7.4)
    table: {
        showColumns: ['title', 'isBasic', 'formatting', 'code', 'sorting'],
    },
    // Фільтри легасі: Ід · Назва
    filter: {
        includeFields: ['id', 'title'],
    },
    places: {
        top: <TopMenu current="currencies" />,
    },
    restUrl: '/services/currencies',
    pageUrl: '/settings/currencies',
    primaryFieldCode: 'code',
    pageConfig: {
        title: 'Валюти',
        icon: <TbCoins />,
        accessFn: CheckDictionaryPageAccessTask,
    },
    settings: {
        searchFields: ['id', 'code', 'title'],
    },
}
